import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { payConfigGateway } from '../index';
import { deductionAmountTypeLabels, deductionTaxTypeLabels, earningTaxTypeLabels, type DeductionCodeResponse, type EarningCodeResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

/** No module in this build has a recorded payroll-SME sign-off for any code's tax treatment yet (see
 * docs/module-readiness.md) — every code shows this warning until that review happens. */
function UnverifiedTaxBadge() {
  return <span className="badge badge--warning">UNVERIFIED — NOT APPROVED FOR PAYROLL CALCULATION</span>;
}

export function EarningsDeductionsPage() {
  const [earningCodes, setEarningCodes] = useState<EarningCodeResponse[]>([]);
  const [deductionCodes, setDeductionCodes] = useState<DeductionCodeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showEarningForm, setShowEarningForm] = useState(false);
  const [earningName, setEarningName] = useState('');
  const [earningTaxType, setEarningTaxType] = useState(1);

  const [showDeductionForm, setShowDeductionForm] = useState(false);
  const [deductionName, setDeductionName] = useState('');
  const [deductionTaxType, setDeductionTaxType] = useState(1);
  const [deductionAmountType, setDeductionAmountType] = useState(1);

  // Inline row editing — only one row (earning or deduction) at a time, tracked by id plus a local draft
  // of just the editable fields (name/tax type/amount type). Saving preserves every other field already
  // on the code (W2 code, exemptions, GL default, IsActive) exactly as handleToggle*/create already do.
  const [editingEarningId, setEditingEarningId] = useState<string | null>(null);
  const [earningDraftName, setEarningDraftName] = useState('');
  const [earningDraftTaxType, setEarningDraftTaxType] = useState(1);

  const [editingDeductionId, setEditingDeductionId] = useState<string | null>(null);
  const [deductionDraftName, setDeductionDraftName] = useState('');
  const [deductionDraftTaxType, setDeductionDraftTaxType] = useState(1);
  const [deductionDraftAmountType, setDeductionDraftAmountType] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [earnings, deductions] = await Promise.all([
        payConfigGateway.listEarningCodes(),
        payConfigGateway.listDeductionCodes(),
      ]);
      setEarningCodes(earnings);
      setDeductionCodes(deductions);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load earnings and deductions.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function handleAddEarning(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await payConfigGateway.createEarningCode({
        name: earningName, taxType: earningTaxType, w2Code: null,
        exemptFederal: null, exemptFica: null, exemptState: null, glAccountDefault: null,
      });
      setEarningName(''); setShowEarningForm(false);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not add earning code.'));
    }
  }

  async function handleAddDeduction(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await payConfigGateway.createDeductionCode({
        name: deductionName, taxType: deductionTaxType, amountType: deductionAmountType,
        defaultAmount: null, defaultCompanyContribution: null, w2Code: null,
        exemptFederal: null, exemptFica: null, exemptState: null, glAccountDefault: null,
      });
      setDeductionName(''); setShowDeductionForm(false);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not add deduction code.'));
    }
  }

  async function handleToggleEarning(code: EarningCodeResponse) {
    setError(null);
    try {
      await payConfigGateway.updateEarningCode(code.id, {
        name: code.name, taxType: code.taxType, w2Code: code.w2Code,
        exemptFederal: code.exemptFederal, exemptFica: code.exemptFica, exemptState: code.exemptState,
        glAccountDefault: code.glAccountDefault, isActive: !code.isActive,
      });
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not change status.'));
    }
  }

  async function handleToggleDeduction(code: DeductionCodeResponse) {
    setError(null);
    try {
      await payConfigGateway.updateDeductionCode(code.id, {
        name: code.name, taxType: code.taxType, amountType: code.amountType,
        defaultAmount: code.defaultAmount, defaultCompanyContribution: code.defaultCompanyContribution,
        w2Code: code.w2Code, exemptFederal: code.exemptFederal, exemptFica: code.exemptFica,
        exemptState: code.exemptState, glAccountDefault: code.glAccountDefault, isActive: !code.isActive,
      });
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not change status.'));
    }
  }

  function startEditEarning(code: EarningCodeResponse) {
    setError(null);
    setEditingEarningId(code.id);
    setEarningDraftName(code.name);
    setEarningDraftTaxType(code.taxType);
  }

  function startEditDeduction(code: DeductionCodeResponse) {
    setError(null);
    setEditingDeductionId(code.id);
    setDeductionDraftName(code.name);
    setDeductionDraftTaxType(code.taxType);
    setDeductionDraftAmountType(code.amountType);
  }

  async function handleSaveEarning(code: EarningCodeResponse) {
    setError(null);
    try {
      await payConfigGateway.updateEarningCode(code.id, {
        name: earningDraftName, taxType: earningDraftTaxType, w2Code: code.w2Code,
        exemptFederal: code.exemptFederal, exemptFica: code.exemptFica, exemptState: code.exemptState,
        glAccountDefault: code.glAccountDefault, isActive: code.isActive,
      });
      setEditingEarningId(null);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save earning code.'));
    }
  }

  async function handleSaveDeduction(code: DeductionCodeResponse) {
    setError(null);
    try {
      await payConfigGateway.updateDeductionCode(code.id, {
        name: deductionDraftName, taxType: deductionDraftTaxType, amountType: deductionDraftAmountType,
        defaultAmount: code.defaultAmount, defaultCompanyContribution: code.defaultCompanyContribution,
        w2Code: code.w2Code, exemptFederal: code.exemptFederal, exemptFica: code.exemptFica,
        exemptState: code.exemptState, glAccountDefault: code.glAccountDefault, isActive: code.isActive,
      });
      setEditingDeductionId(null);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save deduction code.'));
    }
  }

  async function handleDeleteEarning(code: EarningCodeResponse) {
    setError(null);
    if (!window.confirm(`Delete "${code.name}"? This cannot be undone.`)) return;
    try {
      await payConfigGateway.deleteEarningCode(code.id);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not delete earning code.'));
    }
  }

  async function handleDeleteDeduction(code: DeductionCodeResponse) {
    setError(null);
    if (!window.confirm(`Delete "${code.name}"? This cannot be undone.`)) return;
    try {
      await payConfigGateway.deleteDeductionCode(code.id);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not delete deduction code.'));
    }
  }

  if (loading) {
    return <section className="card card--wide"><p className="muted">Loading…</p></section>;
  }

  return (
    <>
      <section className="card card--wide">
        <div className="section-header">
          <h2>Earning Codes</h2>
          <button type="button" onClick={() => setShowEarningForm((v) => !v)}>{showEarningForm ? 'Cancel' : 'Add earning code'}</button>
        </div>
        {error && <p className="error">{error}</p>}
        {showEarningForm && (
          <form className="inline-form" onSubmit={handleAddEarning}>
            <input placeholder="Name" value={earningName} onChange={(e) => setEarningName(e.target.value)} required />
            <select value={earningTaxType} onChange={(e) => setEarningTaxType(Number(e.target.value))}>
              {Object.entries(earningTaxTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <button type="submit">Create</button>
          </form>
        )}
        <table>
          <thead><tr><th>Name</th><th>Tax type</th><th>Tax treatment</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {earningCodes.map((c) => {
              const isEditing = editingEarningId === c.id;
              return (
                <tr key={c.id}>
                  {isEditing ? (
                    <>
                      <td><input value={earningDraftName} onChange={(e) => setEarningDraftName(e.target.value)} /></td>
                      <td>
                        <select value={earningDraftTaxType} onChange={(e) => setEarningDraftTaxType(Number(e.target.value))}>
                          {Object.entries(earningTaxTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </td>
                      <td><UnverifiedTaxBadge /></td>
                      <td>{c.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button type="button" className="link" onClick={() => void handleSaveEarning(c)}>Save</button>
                        <button type="button" className="link" onClick={() => setEditingEarningId(null)}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{c.name}</td>
                      <td>{earningTaxTypeLabels[c.taxType]}</td>
                      <td><UnverifiedTaxBadge /></td>
                      <td>{c.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button type="button" className="link" onClick={() => startEditEarning(c)}>Edit</button>
                        <button type="button" className="link" onClick={() => void handleToggleEarning(c)}>{c.isActive ? 'Deactivate' : 'Activate'}</button>
                        <button
                          type="button"
                          className="link"
                          onClick={() => void handleDeleteEarning(c)}
                          disabled={c.isActive}
                          title={c.isActive ? 'Deactivate before deleting' : undefined}
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {earningCodes.length === 0 && <tr><td colSpan={5} className="muted">No earning codes yet.</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="card card--wide">
        <div className="section-header">
          <h2>Deduction Codes</h2>
          <button type="button" onClick={() => setShowDeductionForm((v) => !v)}>{showDeductionForm ? 'Cancel' : 'Add deduction code'}</button>
        </div>
        {showDeductionForm && (
          <form className="inline-form" onSubmit={handleAddDeduction}>
            <input placeholder="Name" value={deductionName} onChange={(e) => setDeductionName(e.target.value)} required />
            <select value={deductionTaxType} onChange={(e) => setDeductionTaxType(Number(e.target.value))}>
              {Object.entries(deductionTaxTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select value={deductionAmountType} onChange={(e) => setDeductionAmountType(Number(e.target.value))}>
              {Object.entries(deductionAmountTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <button type="submit">Create</button>
          </form>
        )}
        <table>
          <thead><tr><th>Name</th><th>Tax type</th><th>Tax treatment</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {deductionCodes.map((c) => {
              const isEditing = editingDeductionId === c.id;
              return (
                <tr key={c.id}>
                  {isEditing ? (
                    <>
                      <td><input value={deductionDraftName} onChange={(e) => setDeductionDraftName(e.target.value)} /></td>
                      <td>
                        <select value={deductionDraftTaxType} onChange={(e) => setDeductionDraftTaxType(Number(e.target.value))}>
                          {Object.entries(deductionTaxTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                        <select value={deductionDraftAmountType} onChange={(e) => setDeductionDraftAmountType(Number(e.target.value))}>
                          {Object.entries(deductionAmountTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </td>
                      <td><UnverifiedTaxBadge /></td>
                      <td>{c.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button type="button" className="link" onClick={() => void handleSaveDeduction(c)}>Save</button>
                        <button type="button" className="link" onClick={() => setEditingDeductionId(null)}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{c.name}</td>
                      <td>{deductionTaxTypeLabels[c.taxType]}</td>
                      <td><UnverifiedTaxBadge /></td>
                      <td>{c.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button type="button" className="link" onClick={() => startEditDeduction(c)}>Edit</button>
                        <button type="button" className="link" onClick={() => void handleToggleDeduction(c)}>{c.isActive ? 'Deactivate' : 'Activate'}</button>
                        <button
                          type="button"
                          className="link"
                          onClick={() => void handleDeleteDeduction(c)}
                          disabled={c.isActive}
                          title={c.isActive ? 'Deactivate before deleting' : undefined}
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {deductionCodes.length === 0 && <tr><td colSpan={5} className="muted">No deduction codes yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </>
  );
}
